import axios from 'axios'
import type {
  BusinessError,
  ErrorDataResponse,
  NetworkError,
  ServerError
} from 'typings/serverError'

/**
 * Returns first instance of business error for given ServerError or null
 */
export const getBusinessError = (error: ServerError): BusinessError | null =>
  error.response?.businessErrors?.[0] || null

/**
 * Returns true if error has the properties of a ServerError interface
 */
export const isServerError = (error: unknown): error is ServerError => {
  return (
    typeof error === 'object' && 'statusCode' in error && 'description' in error
  )
}

/**
 * Returns true if error is has the properties of a NetworkError interface
 */
export const isNetworkError = (error: unknown): error is NetworkError => {
  return (
    axios.isAxiosError<ErrorDataResponse>(error) &&
    'code' in error &&
    'message' in error &&
    'trackingId' in error
  )
}

/**
 * Returns error response code found in a ServerError. IMPORTANT! Only use with
 * new responseCodes for v3 auth login.
 */
export const getErrorMessage = (serverError: ServerError): string => {
  let message: string

  if (serverError.statusCode === 422) {
    // business error
    message = serverError.businessErrorCode
  } else if (serverError.statusCode === 400) {
    // other network errors
    message = serverError.response?.errors?.[0]?.field
  }

  return message || ''
}

const parseError = (err: NetworkError): ServerError => {
  const {
    status,
    message,
    response: { status: responseStatus, data: responseData }
  } = err

  const defaultServerError: ServerError = {
    statusCode: responseStatus || status || 500,
    description: responseData.message || message,
    businessErrorCode: '',
    response: responseData || null
  }

  if ('businessErrors' in responseData) {
    const { code: businessErrorCode, message: businessErrorMessage } =
      responseData.businessErrors[0]

    return {
      ...defaultServerError,
      description: businessErrorMessage,
      businessErrorCode
    }
  }

  if ('errors' in responseData) {
    const { reason } = responseData.errors[0]

    return {
      ...defaultServerError,
      description: reason
    }
  }

  return defaultServerError
}

export default parseError
